"use client";

import { Users, Trophy, Crown } from "lucide-react";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { AgentProfileCard } from "./AgentProfileCard";
import { getInitials, statusColor, statusLabel } from "./helpers";
import type { ChannelMember } from "./TeamChatService";

export interface MemberListProps {
  members: ChannelMember[];
  presenceMap: Record<string, { status: string; lastSeen?: string }>;
  onStartDm: (userId: string, userName: string) => void;
  isAnnouncementChannel?: boolean;
  isDm?: boolean;
  agencyList?: Array<{ id: string; name: string }>;
}

export function MemberList({
  members,
  presenceMap,
  onStartDm,
  isAnnouncementChannel,
  isDm,
  agencyList,
}: MemberListProps) {
  const isUserOnline = (m: ChannelMember) => {
    const presence = presenceMap[m.userId];
    if (presence) return ["online", "available", "idle", "dnd", "on-call", "on_call"].includes(presence.status);
    return m.user.isOnline || ["available", "online", "on-call", "idle", "dnd"].includes(m.user.userStatus || "");
  };
  const getUserStatus = (m: ChannelMember) => {
    const presence = presenceMap[m.userId];
    return presence?.status || m.user.userStatus || (isUserOnline(m) ? "online" : "offline");
  };
  const online = members.filter(isUserOnline);
  const offline = members.filter(m => !isUserOnline(m));

  // Only show agency names when members are from multiple agencies (agency-wide mode)
  const uniqueAgencies = new Set(members.map(m => m.agencyName).filter(Boolean));
  const isMultiAgency = uniqueAgencies.size > 1;

  const agencyRoleBadge = (role?: string) => {
    if (role === "owner") return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">OWNER</span>;
    if (role === "admin") return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">ADMIN</span>;
    return null;
  };

  const rankMedal = (rank?: number | null) => {
    if (rank === 1) return <span title="#1 This Month" className="text-base leading-none">{"\uD83E\uDD47"}</span>;
    if (rank === 2) return <span title="#2 This Month" className="text-base leading-none">{"\uD83E\uDD48"}</span>;
    if (rank === 3) return <span title="#3 This Month" className="text-base leading-none">{"\uD83E\uDD49"}</span>;
    return null;
  };

  const shortName = (name?: string | null) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length <= 1) return name;
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  };

  // Show top 3 leaderboard at top of member list
  const topPerformers = members
    .filter(m => m.leaderboardRank && m.leaderboardRank <= 3)
    .sort((a, b) => (a.leaderboardRank || 99) - (b.leaderboardRank || 99));

  const renderMember = (m: ChannelMember) => {
    const status = getUserStatus(m);
    const isOnline = isUserOnline(m);
    return (
      <Popover key={m.id}>
        <PopoverTrigger asChild>
          <button className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-colors text-left">
            <div className="relative">
              <Avatar className="w-7 h-7">
                <AvatarImage src={m.user.profileImageUrl} />
                <AvatarFallback className="text-[10px] bg-zinc-200 dark:bg-zinc-700 font-semibold">
                  {getInitials(m.user.name || m.user.email)}
                </AvatarFallback>
              </Avatar>
              <span aria-label={statusLabel(status, isOnline)} className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-zinc-900 ${statusColor(status, isOnline)}`} />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate flex-shrink min-w-0">
                  {shortName(m.user.name) || m.user.email}
                </span>
                <span className="flex-shrink-0">{rankMedal(m.leaderboardRank)}</span>
                <span className="flex-shrink-0">{agencyRoleBadge(m.agencyRole)}</span>
              </div>
              <p className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">
                {isMultiAgency && m.agencyName
                  ? m.agencyName
                  : m.agencyTitle || statusLabel(status, isOnline)}
              </p>
            </div>
            {m.role === "owner" && (
              <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="agent-profile-pop w-auto p-0 border-none shadow-2xl" side="left" align="start" sideOffset={8}>
          <AgentProfileCard
            member={m}
            presenceStatus={presenceMap[m.userId]}
            onDm={() => onStartDm(m.userId, m.user.name || m.user.email)}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div role="complementary" aria-label="Team members" className="h-full border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Agents — {members.length}
          <span className="ml-auto text-[10px] font-normal text-zinc-400 dark:text-zinc-500 truncate">
            <span className="text-green-500">{online.length} on</span>
            {' · '}
            <span>{offline.length} off</span>
          </span>
        </h3>
        {isAnnouncementChannel && (
          <p className="text-[10px] text-amber-500/70 dark:text-amber-400/60 mt-1 flex items-center gap-1">
            <span>🔒</span> Only you can see this list
          </p>
        )}
      </div>
      <ScrollArea className="h-[calc(100%-48px)]">
        <div className="p-2">
          {/* Top performers this month — channels only; a private DM (possibly
              cross-agency) shouldn't broadcast the leaderboard */}
          {!isDm && topPerformers.length > 0 && (
            <div className="mb-3 mx-1 p-2 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/10 border border-amber-200/50 dark:border-amber-800/30 overflow-hidden">
              <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Trophy className="w-3 h-3" /> Top Closers
              </p>
              {topPerformers.map((m, i) => (
                <div key={m.id} className="flex items-center gap-1.5 py-0.5">
                  <span className="text-sm leading-none flex-shrink-0">{i === 0 ? "\uD83E\uDD47" : i === 1 ? "\uD83E\uDD48" : "\uD83E\uDD49"}</span>
                  <span className="text-xs font-semibold text-amber-900 dark:text-amber-200 truncate">
                    {shortName(m.user.name) || m.user.email}
                  </span>
                </div>
              ))}
            </div>
          )}

          {online.length > 0 && (
            <div role="list" aria-label="Online members" className="mb-3">
              <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider px-3 mb-1">
                Online — {online.length}
              </p>
              {online.map(renderMember)}
            </div>
          )}
          {offline.length > 0 && (
            <div role="list" aria-label="Offline members">
              <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider px-3 mb-1">
                Offline — {offline.length}
              </p>
              {offline.map(renderMember)}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
